// Runtime LESS recompile of the dynamic theme template with a client-supplied
// base hex.
//
// Clients that supply a base_hex still report skin === 0, so the DOM root
// carries class "theme-0". We render _theme-0-dynamic.less in that case: a
// purpose-built duplicate of theme-0 where every branded hardcoded colour has
// been replaced with @brand-primary so a single modifyVars seed recolours the
// full palette. The compiled CSS is injected into a single <style> tag that
// wins against the static build output by virtue of ordering.

// Raw-string imports of the LESS sources. Concatenated in the same order as
// assets/themes.less so mixins are defined before they are called.
import standardSrc       from "!!raw-loader!@/assets/themes/_standard.less";
import darkSrc           from "!!raw-loader!@/assets/themes/_dark.less";
import dfdSrc            from "!!raw-loader!@/assets/themes/_dfd.less";
import therapyPortalSrc  from "!!raw-loader!@/assets/themes/_therapy-portal.less";
import dynamicSrc        from "!!raw-loader!@/assets/themes/_theme-0-dynamic.less";

// raw-loader returns { default: "..." } in some configurations and the string
// directly in others (depends on esModule flag / interop). Normalise.
const asString = mod => (typeof mod === "string" ? mod : mod.default);

const THEME_SOURCE = [
  asString(standardSrc),
  asString(darkSrc),
  asString(dfdSrc),
  asString(therapyPortalSrc),
  asString(dynamicSrc),
].join("\n");

const STYLE_TAG_ID = "theme-dynamic-override";

// One seed colour recolours everything in _theme-0-dynamic.less because every
// branded hex and every other @brand-* usage has been collapsed onto
// @brand-primary.
const MODIFY_VAR_NAMES = ["brand-primary"];

let lessPromise = null;
function loadLess() {
  if (!lessPromise) {
    lessPromise = import(/* webpackChunkName: "less-runtime" */ "less");
  }
  return lessPromise;
}

export async function applyDynamicTheme(baseHex) {
  if (typeof baseHex !== "string" || !/^#[0-9a-f]{6}$/i.test(baseHex)) {
    clearDynamicTheme();
    return;
  }
  const lessModule = await loadLess();
  const less = lessModule.default || lessModule;
  const modifyVars = {};
  // Append !important so every emitted rule carries the flag. Theme-0's
  // static build emits `@brand-primary: @dfd-primary !important;` which
  // forces !important on every usage; without matching the flag our override
  // loses the cascade to the static rules even though it loads later.
  MODIFY_VAR_NAMES.forEach(name => { modifyVars[name] = `${baseHex} !important`; });
  try {
    const { css } = await less.render(THEME_SOURCE, { modifyVars });
    let styleEl = document.getElementById(STYLE_TAG_ID);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = STYLE_TAG_ID;
    }
    styleEl.textContent = css;
    // Always re-append so the override is the last stylesheet in <head> and
    // wins the cascade against any subsequently-injected component styles
    // (vue-style-loader appends throughout runtime in dev mode).
    document.head.appendChild(styleEl);
  } catch (e) {
    console.error("[dynamicTheme] compile failed", e);
  }
}

export function clearDynamicTheme() {
  const styleEl = document.getElementById(STYLE_TAG_ID);
  if (styleEl) styleEl.remove();
}
